/**
 * Voice mode host for the PWA: runs the pure state machine (machine.ts) and performs its effects
 * with the Web Speech API — recognition in, synthesis out. Recognition is on-device only
 * (`processLocally`, lib/call/local-speech.ts): where the browser can't guarantee that, the page
 * falls back to typing. Audio never goes to a browser vendor's cloud recognizer. A native iOS/watchOS host implements the same effects with
 * SFSpeechRecognizer / AVSpeechSynthesizer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type LocalRecognition, speechLang, startLocalRecognition } from '../call/local-speech.js'
import { ctor as recognitionCtor } from '../speech.js'
import {
  initialVoice,
  type VoiceEffect,
  type VoiceEvent,
  type VoiceState,
  voiceReducer,
} from './machine.js'
import { browserSynth, SentenceStream, Speaker, speakable } from './tts.js'

/** One turn: say something (whole or streamed), or finish silently. */
export interface VoiceTurn {
  readonly signal: AbortSignal
  /** Speak a complete reply. */
  say(text: string): void
  /** Speak a growing reply as it streams; `done` closes it. */
  stream(fullSoFar: string, done: boolean): void
  /** Finished without speaking. */
  done(): void
}
export type TurnHandler = (text: string, turn: VoiceTurn) => void | Promise<void>

export function useVoice(onUtterance: TurnHandler) {
  const [state, setState] = useState<VoiceState>(initialVoice)
  const stateRef = useRef(state)
  const handler = useRef(onUtterance)
  handler.current = onUtterance
  const rec = useRef<LocalRecognition | null>(null)
  const turn = useRef<AbortController | null>(null)
  const synth = useMemo(() => browserSynth(), [])
  const recognitionSupported = !!recognitionCtor()
  const dispatchRef = useRef<(e: VoiceEvent) => void>(() => {})

  const speaker = useMemo(
    () =>
      synth
        ? new Speaker({
            synth,
            utterance: (text) => new SpeechSynthesisUtterance(text),
            onIdle: () => dispatchRef.current({ type: 'SPEECH_DONE' }),
            voice: () => ({ lang: navigator.language || 'en-US', rate: 1.05 }),
          })
        : undefined,
    [synth],
  )

  const listen = useCallback(() => {
    const C = recognitionCtor()
    if (!C) return
    rec.current?.abort()
    let r: LocalRecognition | null = null
    try {
      // On-device only: `processLocally` is set before start and errors fail closed.
      r = startLocalRecognition(C, speechLang(), {
        onFinal: (text) => dispatchRef.current({ type: 'FINAL', text }),
        onInterim: (text) => {
          if (text) dispatchRef.current({ type: 'INTERIM', text })
        },
        onError: (e) => dispatchRef.current({ type: 'RECOGNITION_ERROR', error: e.reason }),
        onEnd: () => {
          if (rec.current === r) {
            rec.current = null
            dispatchRef.current({ type: 'RECOGNITION_END' })
          }
        },
      })
    } catch (e) {
      dispatchRef.current({
        type: 'RECOGNITION_ERROR',
        error: e instanceof Error ? e.message : 'not-local',
      })
      return
    }
    rec.current = r
  }, [])

  const run = useCallback(
    (fx: VoiceEffect) => {
      switch (fx.type) {
        case 'listen':
          return listen()
        case 'stopListening':
          return rec.current?.stop()
        case 'abortListening': {
          const r = rec.current
          rec.current = null
          return r?.abort()
        }
        case 'speak':
          return speaker ? speaker.say(fx.text) : undefined
        case 'cancelSpeech':
          return speaker?.cancel()
        case 'cancelTurn':
          turn.current?.abort()
          turn.current = null
          return
        case 'handle': {
          turn.current?.abort()
          const ac = new AbortController()
          turn.current = ac
          speaker?.begin()
          const sentences = new SentenceStream()
          let spoke = false
          const live = () => !ac.signal.aborted && turn.current === ac
          const say = (chunk: string) => {
            if (!live() || !chunk.trim()) return
            spoke = true
            dispatchRef.current({ type: 'SPEAK', text: speakable(chunk) })
          }
          const finish = () => {
            if (!live()) return
            if (!spoke || !speaker) {
              // Nothing audible (or no synthesis): the caption alone is the reply.
              dispatchRef.current(spoke ? { type: 'SPEECH_DONE' } : { type: 'TURN_DONE' })
            } else speaker.close()
          }
          const t: VoiceTurn = {
            signal: ac.signal,
            say: (text) => {
              say(text)
              finish()
            },
            stream: (full, done) => {
              for (const s of done ? sentences.end(full) : sentences.push(full)) say(s)
              if (done) finish()
            },
            done: finish,
          }
          void Promise.resolve(handler.current(fx.text, t)).catch((e: unknown) =>
            t.say(`Sorry, that failed: ${e instanceof Error ? e.message : 'unknown error'}.`),
          )
          return
        }
      }
    },
    [listen, speaker],
  )

  const dispatch = useCallback(
    (e: VoiceEvent) => {
      const r = voiceReducer(stateRef.current, e)
      stateRef.current = r.state
      setState(r.state)
      for (const fx of r.effects) run(fx)
    },
    [run],
  )
  dispatchRef.current = dispatch

  useEffect(
    () => () => {
      rec.current?.abort()
      turn.current?.abort()
      speaker?.cancel()
    },
    [speaker],
  )

  return {
    state,
    dispatch,
    recognitionSupported,
    synthesisSupported: !!synth,
    /** Typed fallback (and tests): hand text to the turn handler as if it was heard. */
    submitText: (text: string) => dispatch({ type: 'TEXT', text }),
  }
}
